import invariant from 'tiny-invariant'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Text,
  Button,
  ErrorMessage,
  InfoBox,
  InfoRow,
  Primary13Regular,
  Primary14ExtendedSemiBold,
  GreyGrey100,
  PrimaryPrimary80,
  RedRed60,
  SimpleModal,
  usePersistMagicTableQueryParams,
  Box,
  Flex
} from '@sequencehq/core-components'
import JSONPretty from 'react-json-pretty'
import { useQuery } from '@sequencehq/api/utils'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useMemo, useState } from 'react'
import { OverwriteSeatEventDrawer } from './OverwriteSeatEvent/overwriteSeatEvent.drawer'
import { ProtectedButton } from 'lib/rbac'
import { SeatTypeBadge } from 'Seats/common/SeatTypeBadge'
import { CustomerPreviewCardPill } from 'Customer/components/CustomerPreviewCard'
import { useUnmappedAliasResolution } from 'Customer/index'

export const ViewSeatEvent = () => {
  const { snapshotId } = useParams<{ snapshotId: string }>()
  const navigate = useNavigate()
  const { persistMagicTableQueryParams } = usePersistMagicTableQueryParams()
  const [isOverwriteDrawerOpen, setIsOverwriteDrawerOpen] = useState(false)

  invariant(snapshotId, 'snapshotId is required')

  const seatEventQuery = useQuery(dashboard20240730Client.getSeatEvent, {
    id: snapshotId
  })

  const handleClose = () => {
    navigate(persistMagicTableQueryParams('/seat-events'))
  }

  const handleOverwriteClose = () => {
    navigate(persistMagicTableQueryParams('/seat-events'))
  }

  const seatEvent = seatEventQuery.data

  const customerQuery = useQuery(
    dashboard20240730Client.getCustomerByAlias,
    seatEvent ? { alias: seatEvent.customerAlias } : undefined,
    // A 404 is expected when the alias hasn't been mapped to a customer yet, so
    // don't retry — we surface the linking actions instead.
    { retry: false }
  )

  const isAliasUnmapped = Boolean(
    seatEvent?.customerAlias && !customerQuery.isPending && !customerQuery.data
  )

  const { banner, linkButtons, linkModals } = useUnmappedAliasResolution({
    alias: seatEvent?.customerAlias ?? '',
    testIdPrefix: 'seatEvent'
  })

  const filteredSeatEvent = useMemo(() => {
    if (!seatEvent) return {}
    return Object.keys(seatEvent).reduce<Record<string, unknown>>(
      (acc, key) => {
        if (['sequenceAccountId', 'sequenceUserId'].includes(key)) {
          return acc
        }
        return {
          ...acc,
          [key]: seatEvent[key as keyof typeof seatEvent]
        }
      },
      {}
    )
  }, [seatEvent])

  if (isOverwriteDrawerOpen && seatEvent) {
    return (
      <OverwriteSeatEventDrawer
        isOpen={true}
        onClose={handleOverwriteClose}
        seatEvent={seatEvent}
      />
    )
  }

  return (
    <SimpleModal
      isOpen={true}
      variant="drawer"
      title={seatEvent?.id || 'Seat event'}
      cancelButtonText="Close"
      onClose={handleClose}
      showSubmitButton={false}
      isLoading={seatEventQuery.isPending}
      data-testid="viewSeatEvent.drawer"
      closeOnUpdate={false}
    >
      {!seatEvent ? (
        <Flex alignItems="center" justifyContent="center" height="100%">
          <ErrorMessage />
        </Flex>
      ) : (
        <Flex direction="column" gap="20px" height="100%" overflow="hidden">
          {isAliasUnmapped && <Box flexShrink={0}>{banner}</Box>}
          <Box flexShrink={0}>
            <Text {...Primary14ExtendedSemiBold} marginBottom="8px">
              Details
            </Text>
            <InfoBox data-testid="seatEvent.info">
              <InfoRow label="Event ID">
                <Text
                  {...Primary13Regular}
                  color={GreyGrey100}
                  data-testid="seatEvent.inspector.eventId"
                >
                  {seatEvent.customerEventId}
                </Text>
              </InfoRow>
              <InfoRow label="Timestamp">
                <Text
                  {...Primary13Regular}
                  color={GreyGrey100}
                  data-testid="seatEvent.inspector.timestamp"
                >
                  {seatEvent.eventTimestamp}
                </Text>
              </InfoRow>
              <InfoRow label="Seat type">
                <SeatTypeBadge
                  seatType={seatEvent.seatType}
                  data-testid="seatEvent.inspector.seatType"
                />
              </InfoRow>
              {customerQuery.data && (
                <InfoRow label="Customer">
                  <CustomerPreviewCardPill
                    customerId={customerQuery.data.id}
                    variant="ghost"
                    data-testid="seatEvent.inspector.customer"
                  />
                </InfoRow>
              )}
              <InfoRow label="Customer alias">
                <Text
                  {...Primary13Regular}
                  color={GreyGrey100}
                  data-testid="seatEvent.inspector.customerAlias"
                >
                  {seatEvent.customerAlias}
                </Text>
              </InfoRow>
              <InfoRow label="Total (new balance)">
                <Text
                  {...Primary13Regular}
                  color={GreyGrey100}
                  data-testid="seatEvent.inspector.total"
                >
                  {seatEvent.total}
                </Text>
              </InfoRow>
            </InfoBox>
          </Box>

          <Flex flexShrink={0} direction="column" gap="8px">
            {isAliasUnmapped && linkButtons}
            <ProtectedButton permission="org.usage:update">
              <Button
                variant="secondary"
                onClick={() => setIsOverwriteDrawerOpen(true)}
                data-testid="seatEvent.overwrite"
                fullWidth
              >
                Overwrite event
              </Button>
            </ProtectedButton>
          </Flex>

          <Box>
            <Text
              {...Primary14ExtendedSemiBold}
              marginBottom="8px"
              flexShrink={0}
            >
              Data
            </Text>
            <InfoBox data-testid="seatEvent.data" overflow="auto" padding="8px">
              <JSONPretty
                id="json-pretty"
                data={filteredSeatEvent}
                theme={{
                  main: 'font-size: 14px; line-height: 20px;',
                  key: `color:${PrimaryPrimary80};`,
                  string: `color:${RedRed60};`
                }}
              />
            </InfoBox>
          </Box>

          {linkModals}
        </Flex>
      )}
    </SimpleModal>
  )
}
